"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Coins, Check, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export interface PricingCardProps {
  packId: string;
  coins: number;
  amountUsd: number;
  isPopular?: boolean;
}

export function PricingCard({ packId, coins, amountUsd, isPopular }: PricingCardProps) {
  const [loading, setLoading] = useState(false);

  const pricePerCoin = (amountUsd / coins).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const handleCheckout = () => {
    setLoading(true);
    const searchParams = new window.URLSearchParams(window.location.search);
    const redirect = searchParams.get("redirect");
    let redirectParam = "";
    if (redirect) {
      // Validate redirect is a relative path to prevent Open Redirect
      const isRelative = redirect.startsWith("/") && !redirect.startsWith("//");
      const safeRedirect = isRelative ? redirect : "/browse";
      redirectParam = `&redirect=${encodeURIComponent(safeRedirect)}`;
    }
    window.location.href = `/checkout?packId=${encodeURIComponent(packId)}${redirectParam}`;
  };

  return (
    <div
      className={`relative flex flex-col p-6 rounded-2xl border bg-background/50 backdrop-blur-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isPopular ? "border-primary shadow-primary/20 scale-105 z-10" : "border-border/50"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center">
          <span className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            Most Popular
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${isPopular ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
          <Coins className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-semibold">{coins} Coins</h3>
      </div>

      <div className="mb-2">
        <span className="text-3xl font-bold">${amountUsd.toFixed(2)}</span>
      </div>

      <p className="text-sm text-muted-foreground mb-6 font-medium">
        {pricePerCoin} per coin
      </p>

      <div className="flex-1">
        <ul className="space-y-3 mb-6">
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-500" />
            <span>Unlock Premium Chapters</span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-500" />
            <span>Support your favorite Writers</span>
          </li>
          <li className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-green-500" />
            <span>Never expires</span>
          </li>
        </ul>
      </div>

      <Button
        className="w-full font-bold"
        variant={isPopular ? "default" : "outline"}
        onClick={handleCheckout}
        disabled={loading}
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {loading ? "Processing..." : `Buy ${coins} Coins`}
      </Button>
    </div>
  );
}
